import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/**
 * FloatingAd — closable promo popup that floats over the bottom-right of the
 * homepage. Configurable from Admin → Popups, with audience targeting so a
 * separate creative can be shown to anonymous visitors vs. logged-in users.
 *
 * Dismissal handling: when the visitor closes the popup we set a localStorage
 * timestamp scoped to that popup's id. The popup stays hidden until
 * `dismissal_days` have elapsed (server-configured), then naturally returns.
 * Different popups (different IDs) are tracked independently so a brand-new
 * campaign isn't suppressed by a previous dismissal.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const STORAGE_PREFIX = 'jd_popup_dismissed_';

const isStillDismissed = (popupId, days) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + popupId);
    if (!raw) return false;
    const dismissedAt = parseInt(raw, 10);
    if (Number.isNaN(dismissedAt)) return false;
    const expiresAt = dismissedAt + (Math.max(0, days || 0) * 86400000);
    return Date.now() < expiresAt;
  } catch (_e) {
    return false;
  }
};

export const FloatingAd = () => {
  const { user } = useAuth();
  const [popup, setPopup] = useState(null);
  const [closed, setClosed] = useState(false);
  const [visible, setVisible] = useState(false); // for the slide-in animation

  useEffect(() => {
    const audience = user ? 'user' : 'anonymous';
    let cancelled = false;
    axios
      .get(`${API_URL}/popups/active?audience=${audience}`)
      .then((r) => {
        if (cancelled) return;
        const data = r.data;
        if (!data || !data.id || !data.image_url) return;
        if (isStillDismissed(data.id, data.dismissal_days)) return;
        setPopup(data);
        // Tiny delay so the homepage hero gets the first paint to itself,
        // then we slide the promo in.
        setTimeout(() => !cancelled && setVisible(true), 800);
        // Fire impression once the popup will actually be shown.
        axios.post(`${API_URL}/popups/${data.id}/event`, { event: 'impression' }).catch(() => {});
      })
      .catch(() => { /* silent — popup is non-critical UI */ });
    return () => { cancelled = true; };
  }, [user]);

  const handleClose = () => {
    setVisible(false);
    setClosed(true);
    if (popup?.id) {
      try { localStorage.setItem(STORAGE_PREFIX + popup.id, String(Date.now())); } catch (_e) { /* ignore */ }
      axios.post(`${API_URL}/popups/${popup.id}/event`, { event: 'dismiss' }).catch(() => {});
    }
  };

  const handleClick = () => {
    if (popup?.id) {
      axios.post(`${API_URL}/popups/${popup.id}/event`, { event: 'click' }).catch(() => {});
    }
  };

  if (!popup || closed) return null;

  const inner = (
    <div
      className="relative inline-block"
      data-testid={`floating-ad-${popup.id}`}
    >
      <img
        src={popup.image_url}
        alt={popup.name || 'Special offer'}
        className="block w-full h-auto drop-shadow-2xl"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}
        aria-label="Close"
        className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-black/75 hover:bg-black text-white flex items-center justify-center transition-colors shadow-lg ring-2 ring-white/30"
        data-testid="floating-ad-close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  // Outer positioning + slide-in transition. Bigger by default (480px desktop,
  // ~85% viewport on mobile), capped at 480px so it never overwhelms the page.
  const wrapClass = `fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 transition-all duration-500 ${
    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
  }`;
  const sizeStyle = { width: 'min(85vw, 480px)' };

  if (popup.link_url) {
    // Internal links use react-router; external (http/https) open in a new tab.
    const isExternal = /^https?:\/\//i.test(popup.link_url);
    if (isExternal) {
      return (
        <div className={wrapClass} style={sizeStyle}>
          <a
            href={popup.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            data-testid={`floating-ad-link-${popup.id}`}
            className="block"
          >
            {inner}
          </a>
        </div>
      );
    }
    return (
      <div className={wrapClass} style={sizeStyle}>
        <Link to={popup.link_url} onClick={handleClick} data-testid={`floating-ad-link-${popup.id}`} className="block">
          {inner}
        </Link>
      </div>
    );
  }

  return <div className={wrapClass} style={sizeStyle}>{inner}</div>;
};

export default FloatingAd;
